/* ============================================================================
   BITÁCORA ELECTRÓNICA DEL PILOTO

   ══════════════════════════════════════════════════════════════════════════
   LA REGLA QUE ORDENA TODO ESTE MÓDULO

   **El flight time va al piloto. El air time va a la aeronave.**

   Del mismo cronómetro salen dos números y cada uno tiene su destino:

     FT (flight time) → libro de vuelo del piloto y duty time
     AT (air time)    → bitácora de mantenimiento de la aeronave

   No son intercambiables. El FT del piloto incluye el tiempo con rotores
   girando en tierra —es tiempo de servicio, cuenta para sus límites—. El AT es
   solo el tiempo en el aire, que es lo que consume vida de componentes.

   Usar el FT para mantenimiento infla las horas de la aeronave y adelanta las
   inspecciones. Usar el AT para el piloto le descuenta horas que voló y que
   cuentan para su jornada. Los dos errores son graves y ninguno se nota
   mirando la pantalla.

   **La entrega 73 tenía este error**: ponía el FT en `horasAeronave`, que es
   el campo que suma a mantenimiento.
   ══════════════════════════════════════════════════════════════════════════

   La estructura de campos sigue el libro de vuelo que usa el piloto en papel:
   fecha, marca y modelo, matrícula, ruta, duración, aterrizajes día y noche,
   condiciones de vuelo, y la función a bordo.
   ========================================================================== */

/**
 * Número, aceptando lo que viene de un formulario.
 *
 * ═══ EL ERROR QUE ESTO CORRIGE ═══
 *
 * Antes era `Number.isFinite(v) ? v : null`, que rechaza el TEXTO: un campo de
 * formulario devuelve `"4500"`, no `4500`, y `Number.isFinite("4500")` es
 * falso.
 *
 * Resultado: todo lo que se escribía en el formulario se guardaba en cero. Los
 * aterrizajes del arrastre, las aproximaciones, y —peor— un vuelo cargado a
 * mano con la duración en decimal se **rechazaba entero**, porque el flight
 * time llegaba como texto y parecía ausente.
 *
 * La cadena vacía NO es cero: es «no se declaró». Distinguirlas importa porque
 * cero es una afirmación.
 */
const num = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
const texto = (v) => (typeof v === "string" ? v.trim() : "");

/** A dónde va cada tiempo. No es configurable: es la regla. */
export const DESTINO = {
  ft: {
    campo: "flightTime",
    va: "piloto",
    etiqueta: "Flight time",
    nota: "Libro de vuelo del piloto y duty time. Incluye rotores girando en tierra.",
  },
  at: {
    campo: "airTime",
    va: "aeronave",
    etiqueta: "Air time",
    nota: "Bitácora de mantenimiento. Solo tiempo en el aire: es lo que consume "
      + "vida de componentes.",
  },
};

/** Funciones a bordo, como las registra el libro de vuelo. */
export const FUNCIONES = [
  ["pic", "Piloto al mando (PIC)"],
  ["sic", "Copiloto (SIC)"],
  ["instructor", "Instructor de vuelo"],
  ["dual", "Alumno / dual recibido"],
  /* «Solo» sale también de las funciones a bordo: es de escuela y en
     operación comercial no se usa. Un asiento viejo que la tenga se sigue
     leyendo —no se pierde— pero ya no se ofrece al cargar. */
];

/**
 * Las columnas del libro de vuelo del operador.
 *
 * Transcritas de `LIBRO_DE_VUELO_BF_2024`. Se respetan sus grupos porque el
 * piloto las reconoce y porque el total de cada una es lo que arrastra de mes
 * a mes:
 *
 *   FECHA · MARCA Y MODELO · MATRÍCULA · RUTAS (desde/hasta)
 *   DURACIÓN TOTAL DEL VUELO · CAT. AERONAVE (helicóptero)
 *   ATERRIZAJES: día · noche
 *   CONDICIONES DE VUELO: día · noche · instrumentos · capota · APP (nro/tipo)
 *   SIMULADOR
 *   TIPO DE TIEMPO DE PILOTO: cross country · solo · alumno dual ·
 *                             copiloto SIC · piloto PIC · instructor
 *   OBSERVACIONES
 *
 * **Las condiciones y el tipo de tiempo son HORAS, no marcas.** Un vuelo puede
 * tener 2:45 totales con 1:45 de instrumentos adentro: son columnas que se
 * suman por separado, no categorías excluyentes. Tratarlas como una sola
 * elección —como estaba— pierde la mitad del libro.
 */
export const COLUMNAS_LIBRO = [
  { clave: "flightTime", grupo: "duracion", etiqueta: "Duración total", tipo: "horas" },
  { clave: "catHelicoptero", grupo: "duracion", etiqueta: "Cat. helicóptero", tipo: "horas" },

  /* ═══ LAS ETIQUETAS NO SE PUEDEN REPETIR ═══

     `ldgDia` y `horasDia` se llamaban las dos «Día», y `ldgNoche` y
     `horasNoche` las dos «Noche».

     Dentro del formulario se distinguían por el grupo, pero en la tabla de
     totales —que no muestra grupos— aparecían dos columnas «Día» seguidas:
     una con 27987 aterrizajes y otra con 00:00 horas. El piloto veía un número
     enorme donde esperaba sus horas de vuelo diurno y no podía saber cuál era
     cuál.

     Ahora cada etiqueta dice QUÉ mide. `etiquetaCorta` queda para el
     formulario, donde el grupo ya está a la vista. */
  { clave: "ldgDia", grupo: "aterrizajes", etiqueta: "Aterrizajes día", etiquetaCorta: "Día", tipo: "entero" },
  { clave: "ldgNoche", grupo: "aterrizajes", etiqueta: "Aterrizajes noche", etiquetaCorta: "Noche", tipo: "entero" },

  { clave: "horasDia", grupo: "condiciones", etiqueta: "Horas día", etiquetaCorta: "Día", tipo: "horas" },
  { clave: "horasNoche", grupo: "condiciones", etiqueta: "Horas noche", etiquetaCorta: "Noche", tipo: "horas" },
  { clave: "instrumentosReales", grupo: "condiciones", etiqueta: "Instrumentos reales", etiquetaCorta: "Instrumentos", tipo: "horas" },
  { clave: "instrumentosCapota", grupo: "condiciones", etiqueta: "Instrumentos capota", etiquetaCorta: "Capota", tipo: "horas" },
  { clave: "appNro", grupo: "condiciones", etiqueta: "APP nro.", tipo: "entero" },
  { clave: "appTipo", grupo: "condiciones", etiqueta: "APP tipo", tipo: "texto" },

  { clave: "simulador", grupo: "simulador", etiqueta: "Simulador", tipo: "horas" },

  { clave: "crossCountry", grupo: "tiempo", etiqueta: "Cross country", tipo: "horas" },
  /* ═══ CARGA EXTERNA EN LUGAR DE «SOLO» ═══

     El operador lo pidió, y la razón se sostiene: «solo» es una columna de
     escuela —el alumno volando sin instructor— y en operación comercial no se
     usa nunca. La carga externa, en cambio, es lo que la autoridad y el
     cliente preguntan.

     PERO NO SON LA MISMA CLASE DE COLUMNA, y eso cambia el cálculo:

       «Solo» es una FUNCIÓN: o vuelas solo, o como PIC, o como SIC. Se
       excluyen entre sí y suman al total.

       «Carga externa» es lo que HACES mientras vuelas. Un vuelo de long line
       de 2:00 es 2:00 de PIC **y** 2:00 de carga externa: las dos columnas
       cuentan las mismas horas desde ángulos distintos.

     Por eso sale de la lista de funciones excluyentes. Si quedara, la suma
     daría el doble y saltaría el aviso de «las funciones suman más que el
     total» sobre datos correctos. */
  { clave: "cargaExterna", grupo: "tiempo", etiqueta: "Carga externa (long line)", tipo: "horas" },
  { clave: "dualRecibido", grupo: "tiempo", etiqueta: "Alumno dual", tipo: "horas" },
  { clave: "sic", grupo: "tiempo", etiqueta: "Copiloto SIC", tipo: "horas" },
  { clave: "pic", grupo: "tiempo", etiqueta: "Piloto PIC", tipo: "horas" },
  { clave: "instructor", grupo: "tiempo", etiqueta: "Instructor", tipo: "horas" },
];

/* Las etiquetas tienen que ser únicas: dos columnas con el mismo nombre en una
   tabla sin grupos son indistinguibles, y el piloto no puede saber cuál miró.
   Se comprueba al cargar el módulo, no solo en las pruebas. */
{
  const vistas = new Set();
  for (const c of COLUMNAS_LIBRO) {
    if (vistas.has(c.etiqueta)) {
      throw new Error(`Etiqueta de columna repetida: «${c.etiqueta}». `
        + "Dos columnas con el mismo nombre son indistinguibles en la tabla de totales.");
    }
    vistas.add(c.etiqueta);
  }
}

/** Los grupos, con su nombre para la pantalla. */
export const GRUPOS_LIBRO = [
  ["duracion", "Duración"],
  ["aterrizajes", "Aterrizajes"],
  ["condiciones", "Condiciones de vuelo"],
  ["simulador", "Simulador"],
  ["tiempo", "Tipo de tiempo de piloto"],
];

/**
 * Columnas que ya no se ofrecen al cargar, pero que pueden existir en asientos
 * viejos.
 *
 * `solo` salió del libro cuando el operador la cambió por carga externa. Los
 * asientos que ya la tengan cargada **no se tocan**: sus horas siguen ahí y
 * siguen sumando al total.
 *
 * Lo que no se puede hacer es dejarlas invisibles: una columna guardada que no
 * se muestra en ningún lado es una hora perdida que nadie puede cotejar.
 */
export const COLUMNAS_RETIRADAS = [
  { clave: "solo", grupo: "tiempo", etiqueta: "Solo (retirada)", tipo: "horas" },
];

/** ¿Algún asiento tiene horas en una columna retirada? */
export function columnasRetiradasConDatos(asientos = []) {
  const lista = Array.isArray(asientos) ? asientos : [];
  return COLUMNAS_RETIRADAS.filter((c) => lista.some((a) => (num(a?.[c.clave]) ?? 0) > 0));
}

/** Las columnas de HORAS, que son las que se suman en HH:MM. */
export const COLUMNAS_HORAS = COLUMNAS_LIBRO
  .filter((c) => c.tipo === "horas").map((c) => c.clave);

/** Las de conteo entero. */
export const COLUMNAS_ENTERAS = COLUMNAS_LIBRO
  .filter((c) => c.tipo === "entero").map((c) => c.clave);

/** Condiciones de vuelo del libro. */
export const CONDICIONES = [
  ["dia", "Día"],
  ["noche", "Noche"],
  ["instrumentos", "Instrumentos"],
  ["capota", "Capota"],
];

/** Horas decimales a `HH:MM`, que es como se anota el libro en papel. */
export function aHhMm(horas) {
  const h = num(horas);
  if (h === null || h < 0) return null;
  const total = Math.round(h * 60);
  /* Sin tope de dígitos: un total de carrera pasa de 10000 h y cortarlo a dos
     dígitos mostraría una cifra falsa. */
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** `HH:MM` a horas decimales. El libro se llena en uno y se suma en el otro. */
export function deHhMm(hhmm) {
  const t = texto(hhmm);

  /* HASTA 6 DÍGITOS DE HORAS.

     Estaba en 3, y eso rechazaba «1250:30» — el ejemplo de la propia ayuda del
     formulario. Un piloto con carrera larga tiene cinco dígitos de horas
     totales, y el arrastre es justamente donde se cargan.

     También se aceptan los segundos que trae el libro en Excel («02:45:00»):
     se descartan, porque el libro se lleva en horas y minutos. Rechazar el
     formato obligaría a reescribir a mano cada línea al transcribir. */
  /* ═══ UN NÚMERO SUELTO SON HORAS ═══

     Antes solo se aceptaba `HH:MM`. Escribir «11202» —once mil doscientas dos
     horas, que es como uno dice su total— daba `null`.

     En el total eso producía un error confuso («el arrastre necesita las horas
     totales anteriores», teniendo el campo lleno). Y en las columnas del
     desglose era peor: **se guardaban en cero sin decir nada**. Un piloto que
     escribía «99» en copiloto SIC perdía las 99 horas y no se enteraba.

     Ahora un número suelto son horas, con decimales si hace falta: «2.5» es
     dos horas y media. */
  const conMinutos = /^(\d{1,6}):([0-5]\d)(?::[0-5]\d)?$/.exec(t);
  if (conMinutos) {
    return Math.round((Number(conMinutos[1]) + Number(conMinutos[2]) / 60) * 100) / 100;
  }

  /* Un número suelto: horas enteras o decimales. Se acepta la coma además del
     punto, porque en teclado en español es lo que sale. */
  const suelto = /^\d{1,6}([.,]\d{1,2})?$/.exec(t);
  if (suelto) {
    const n = Number(t.replace(",", "."));
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
  }

  return null;
}

/**
 * Arma el asiento del libro de vuelo del piloto.
 *
 * Toma el **FT**, nunca el AT. Si le llegara el AT, el piloto perdería las
 * horas de rotores girando que sí cuentan para su jornada.
 */
export function asientoDePiloto(vuelo, { ahora = new Date() } = {}) {
  if (!vuelo || typeof vuelo !== "object") {
    return { asiento: null, errores: ["No hay datos del vuelo."], avisos: [] };
  }

  const errores = [];
  const avisos = [];

  const ft = num(vuelo.flightTime) ?? num(vuelo.horasAeronave);
  if (ft === null || ft <= 0) {
    errores.push("Sin flight time no hay asiento de piloto: es el tiempo que cuenta "
      + "para su libro y su jornada.");
  }

  const pilotId = texto(vuelo.pilotId);
  if (!pilotId) errores.push("Falta el piloto.");

  const funcion = texto(vuelo.funcion) || "pic";
  if (!FUNCIONES.some(([c]) => c === funcion)) {
    errores.push(`Función a bordo desconocida: ${funcion}.`);
  }

  const fecha = texto(vuelo.fecha) || (ahora instanceof Date ? ahora : new Date())
    .toISOString().slice(0, 10);

  /* Los aterrizajes se separan día y noche porque el libro los separa y porque
     la habilitación nocturna se mantiene con los de noche. Si no se declara,
     van a día: suponer noche sería regalar currency que no se voló. */
  const ldgDia = num(vuelo.ldgDia) ?? num(vuelo.ldg) ?? 0;
  const ldgNoche = num(vuelo.ldgNoche) ?? 0;

  if (num(vuelo.ldg) !== null && num(vuelo.ldgDia) === null && num(vuelo.ldgNoche) === null) {
    avisos.push("Los aterrizajes se registraron como diurnos. Si alguno fue nocturno, "
      + "corregilo: la habilitación nocturna se mantiene con esos.");
  }

  if (errores.length > 0) return { asiento: null, errores, avisos };

  return {
    asiento: {
      fecha,
      pilotId,
      tipoAeronave: texto(vuelo.tipoAeronave),
      matricula: texto(vuelo.matricula),
      rutaDesde: texto(vuelo.rutaDesde) || null,
      rutaHasta: texto(vuelo.rutaHasta) || null,

      /* ═══ EL NÚMERO DEL PILOTO ES EL FT ═══ */
      flightTime: ft,
      flightTimeHhMm: aHhMm(ft),

      funcion,
      condicion: texto(vuelo.condicion) || "dia",
      ldgDia,
      ldgNoche,
      /* TODAS las columnas del libro, en CERO si no se declararon.

         Cero acá SÍ es correcto —a diferencia de las horas de vuelo— porque
         son columnas de desglose: «cero horas de instrumentos» es una
         afirmación verdadera en un vuelo visual. Lo que no puede quedar en
         cero es la duración total, y esa se valida aparte. */
      ...Object.fromEntries(COLUMNAS_HORAS
        .filter((c) => c !== "flightTime")
        .map((c) => [c, num(vuelo[c]) ?? deHhMm(vuelo[`${c}HhMm`]) ?? 0])),
      ...Object.fromEntries(COLUMNAS_ENTERAS
        .filter((c) => c !== "ldgDia" && c !== "ldgNoche")
        .map((c) => [c, num(vuelo[c]) ?? 0])),
      appTipo: texto(vuelo.appTipo) || null,

      observaciones: texto(vuelo.observaciones) || null,
      origen: vuelo.origen || {},
    },
    errores: [],
    avisos,
  };
}

/**
 * Arma el asiento de mantenimiento de la aeronave.
 *
 * Toma el **AT**, nunca el FT. Si le llegara el FT, las horas de la aeronave
 * se inflarían y las inspecciones se adelantarían.
 */
export function asientoDeAeronave(vuelo) {
  if (!vuelo || typeof vuelo !== "object") {
    return { asiento: null, errores: ["No hay datos del vuelo."], avisos: [] };
  }

  const errores = [];
  const avisos = [];

  const at = num(vuelo.airTime);
  if (at === null || at <= 0) {
    errores.push("Sin air time no hay asiento de mantenimiento: es el tiempo que "
      + "consume vida de componentes.");
  }

  const aircraftId = texto(vuelo.aircraftId);
  if (!aircraftId) errores.push("Falta la aeronave.");

  /* Si el AT es igual al FT, o no hubo tiempo en tierra con rotores —posible en
     un vuelo directo— o alguien copió un número en el otro. Se avisa: es la
     confusión que este módulo existe para evitar. */
  const ft = num(vuelo.flightTime) ?? num(vuelo.horasAeronave);
  if (at !== null && ft !== null && Math.abs(at - ft) < 0.001 && ft > 0) {
    avisos.push("El air time es idéntico al flight time. Verifica que no se haya "
      + "copiado uno en lugar del otro: el AT no debería incluir rotores en tierra.");
  }

  /* Si el FT se corrigió mucho hacia arriba y el AT quedó igual, la diferencia
     se vuelve inverosímil: más de la mitad del vuelo con el motor en marcha y
     sin despegar.

     Puede ser real —una espera larga en plataforma— pero también es lo que
     pasa cuando alguien ajusta el FT contra el reloj de la aeronave y se
     olvida del AT. Mantenimiento se quedaría corto y nadie lo notaría. */
  if (at !== null && ft !== null && ft > 0 && at > 0 && at < ft * 0.5) {
    avisos.push(`El air time (${at} h) es menos de la mitad del flight time (${ft} h). `
      + "Puede ser real si hubo esperas largas en tierra, pero verifica que el AT "
      + "esté actualizado: es el número que suma a mantenimiento.");
  }

  if (at !== null && ft !== null && at > ft + 0.05) {
    errores.push("El air time supera al flight time. El aire es una parte del vuelo, "
      + "no un tiempo aparte: revisa los tramos.");
  }

  if (errores.length > 0) return { asiento: null, errores, avisos };

  return {
    asiento: {
      fecha: texto(vuelo.fecha),
      aircraftId,
      matricula: texto(vuelo.matricula),
      tipoAeronave: texto(vuelo.tipoAeronave),
      pilotId: texto(vuelo.pilotId) || null,

      /* ═══ EL NÚMERO DE LA AERONAVE ES EL AT ═══
         `horasAeronave` es lo que `acumuladoDeAeronave` suma al estado de
         mantenimiento. Va el AIR TIME. */
      horasAeronave: at,
      airTime: at,
      /* El FT se conserva como referencia: sirve para cotejar contra la
         bitácora física, pero NO suma. */
      flightTimeReferencia: ft,

      horasMotor: num(vuelo.horasMotor),
      ciclosNG: num(vuelo.ciclosNG),
      ciclosNF: num(vuelo.ciclosNF),
      ldg: (num(vuelo.ldgDia) ?? 0) + (num(vuelo.ldgNoche) ?? num(vuelo.ldg) ?? 0),
      cargas: num(vuelo.cargas),
      combustibleUsadoKg: num(vuelo.combustibleUsadoKg),
      tipoOperacion: texto(vuelo.tipoOperacion) || "Traslado",

      origen: vuelo.origen || {},
      cotejo: { campos: {}, tecnico: "", fechaCotejo: null, observacion: "" },
    },
    errores: [],
    avisos,
  };
}

/**
 * Del vuelo cerrado salen LOS DOS asientos.
 *
 * Es la operación que automatiza el registro: un cierre de cronómetro produce
 * el asiento del piloto con el FT y el de la aeronave con el AT, sin que nadie
 * transcriba nada.
 */
export function repartirVuelo(vuelo, opciones = {}) {
  const piloto = asientoDePiloto(vuelo, opciones);
  const aeronave = asientoDeAeronave(vuelo);

  return {
    piloto: piloto.asiento,
    aeronave: aeronave.asiento,
    errores: [...piloto.errores, ...aeronave.errores],
    avisos: [...piloto.avisos, ...aeronave.avisos],
    /* Que uno falle no anula el otro: si falta la aeronave, el piloto igual
       tiene que poder registrar sus horas. */
    parcial: Boolean(piloto.asiento) !== Boolean(aeronave.asiento),
  };
}

/**
 * Totales del libro de vuelo, con las columnas que el libro suma.
 *
 * El libro en papel suma por mes y arrastra el total. Acá se hace lo mismo,
 * pero además se puede pedir cualquier período.
 */
export function totalesDeLibro(asientos = [], { desde = null, hasta = null } = {}) {
  const lista = Array.isArray(asientos) ? asientos : [];

  const dentro = (a) => {
    if (!a?.fecha) return false;
    if (desde && a.fecha < desde) return false;
    if (hasta && a.fecha > hasta) return false;
    return true;
  };

  /* El ARRASTRE suma al total pero NO es un vuelo: no tiene fecha de vuelo
     real. Contarlo en «los últimos 90 días» diría que el piloto voló mil horas
     esta semana, y en la currency le daría aterrizajes que no hizo.

     Solo entra cuando NO hay filtro de fechas, que es el total histórico. */
  const sinFiltro = !desde && !hasta;
  const propios = lista.filter((a) => dentro(a) && (sinFiltro || esVuelo(a)));
  const suma = (f) => Math.round(propios.reduce((s, a) => s + (num(f(a)) ?? 0), 0) * 100) / 100;

  const porFuncion = {};
  for (const [clave] of FUNCIONES) {
    porFuncion[clave] = Math.round(
      propios.filter((a) => a.funcion === clave)
        .reduce((s, a) => s + (num(a.flightTime) ?? 0), 0) * 100,
    ) / 100;
  }

  const total = suma((a) => a.flightTime);

  return {
    /* Los arrastres no se cuentan como vuelos ni siquiera en el total: son un
       saldo, y decir «1 vuelo» de 1250 h sería falso. */
    vuelos: propios.filter(esVuelo).length,
    arrastres: propios.filter((a) => !esVuelo(a)).length,
    totalHoras: total,
    totalHhMm: aHhMm(total),
    porFuncion,
    diurno: Math.round(propios.filter((a) => a.condicion === "dia")
      .reduce((s, a) => s + (num(a.flightTime) ?? 0), 0) * 100) / 100,
    nocturno: Math.round(propios.filter((a) => a.condicion === "noche")
      .reduce((s, a) => s + (num(a.flightTime) ?? 0), 0) * 100) / 100,
    /* Cada columna del libro suma por separado, como el total al pie de cada
       hoja del libro en papel. */
    ...Object.fromEntries(COLUMNAS_HORAS
      .filter((c) => c !== "flightTime")
      .map((c) => [c, suma((a) => a[c])])),
    /* Las columnas retiradas se siguen sumando: sus horas existen aunque ya no
       se ofrezcan al cargar. */
    ...Object.fromEntries(COLUMNAS_RETIRADAS.map((c) => [c.clave, suma((a) => a[c.clave])])),
    ...Object.fromEntries(COLUMNAS_ENTERAS.map((c) => [c, suma((a) => a[c])])),
    /* Y los totales de horas también en HH:MM, que es como los lleva el libro. */
    enHhMm: Object.fromEntries([
      ...COLUMNAS_HORAS.map((c) => [c, aHhMm(c === "flightTime" ? total : suma((a) => a[c]))]),
      ...COLUMNAS_RETIRADAS.map((c) => [c.clave, aHhMm(suma((a) => a[c.clave]))]),
    ]),
    /* Los asientos sin fecha no entran en ningún período. Se cuentan aparte
       para que alguien los arregle, en vez de desaparecer. */
    sinFecha: lista.filter((a) => !a?.fecha).length,
  };
}


/* ============================================================================
   PERÍODOS Y CURRENCY

   Es lo que ForeFlight y Garmin Pilot muestran arriba de todo: los totales de
   los últimos 30 y 90 días, los 12 meses, y el total histórico. Y al lado, si
   el piloto está corriente para llevar pasajeros.

   ══════════════════════════════════════════════════════════════════════════
   LOS NÚMEROS DE CURRENCY NO SON UNIVERSALES

   La regla de «tres despegues y aterrizajes en 90 días» viene de la
   reglamentación de EE.UU. Perú y Ecuador tienen las suyas, y el manual de
   operaciones del operador puede ser más exigente.

   Por eso los umbrales son **configurables y vienen marcados como NO
   VERIFICADOS**. La aplicación cuenta y muestra; **cuál es el número que
   aplica lo declara el operador**.

   Mostrar «corriente» con un umbral equivocado es peor que no mostrarlo: un
   piloto que cree estar habilitado y no lo está vuela igual.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/**
 * Umbrales de currency por defecto.
 *
 * `verificado: false` no es un detalle: la pantalla lo muestra y no deja creer
 * que el número salió de la reglamentación del operador.
 */
export const CURRENCY_POR_DEFECTO = {
  dias: 90,
  aterrizajesDia: 3,
  aterrizajesNoche: 3,
  verificado: false,
  fuente: "Valores de referencia habituales. NO verificados contra la "
    + "reglamentación de la autoridad ni contra el manual de operaciones. "
    + "El operador debe declarar los que le aplican.",
};

/** Los períodos que muestra la cabecera, como en las aplicaciones conocidas. */
export const PERIODOS = [
  { clave: "d30", dias: 30, etiqueta: "30 días" },
  { clave: "d90", dias: 90, etiqueta: "90 días" },
  { clave: "m12", dias: 365, etiqueta: "12 meses" },
];

/** Fecha ISO de hace N días. */
function haceDias(hastaISO, dias) {
  const base = Date.parse(`${String(hastaISO).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(base)) return null;
  /* `dias - 1` porque la ventana INCLUYE el día de hoy: «últimos 30 días» son
     hoy y los 29 anteriores. */
  return new Date(base - (dias - 1) * 86400000).toISOString().slice(0, 10);
}

/**
 * La cabecera del libro: totales por período más el histórico.
 */
export function periodosDeLibro(asientos = [], hastaISO = null) {
  const hasta = String(hastaISO || new Date().toISOString().slice(0, 10)).slice(0, 10);

  const porPeriodo = {};
  for (const p of PERIODOS) {
    const desde = haceDias(hasta, p.dias);
    porPeriodo[p.clave] = desde
      ? { ...p, ...totalesDeLibro(asientos, { desde, hasta }) }
      : { ...p, totalHoras: null, vuelos: 0 };
  }

  return {
    hasta,
    ...porPeriodo,
    /* El total histórico va SIN filtro de fechas: es la suma de todo lo
       registrado, que es el número que un piloto conoce de memoria. */
    total: totalesDeLibro(asientos, {}),
  };
}

/**
 * ¿Está corriente para llevar pasajeros?
 *
 * Cuenta los aterrizajes en la ventana y compara. **No afirma habilitación**:
 * dice cuántos hay y cuántos pide el umbral configurado, y si ese umbral está
 * verificado o no.
 */
export function currencyDePiloto(asientos = [], hastaISO = null, umbrales = CURRENCY_POR_DEFECTO) {
  const u = { ...CURRENCY_POR_DEFECTO, ...(umbrales || {}) };
  const hasta = String(hastaISO || new Date().toISOString().slice(0, 10)).slice(0, 10);
  const desde = haceDias(hasta, u.dias);

  if (!desde) {
    return { valido: false, motivo: "Fecha inválida.", verificado: u.verificado };
  }

  const t = totalesDeLibro(asientos, { desde, hasta });

  const evaluar = (hechos, pide) => ({
    hechos,
    pide,
    faltan: Math.max(0, pide - hechos),
    corriente: hechos >= pide,
  });

  const dia = evaluar(t.ldgDia + t.ldgNoche, u.aterrizajesDia);
  const noche = evaluar(t.ldgNoche, u.aterrizajesNoche);

  return {
    valido: true,
    desde,
    hasta,
    dias: u.dias,
    dia,
    noche,
    verificado: u.verificado === true,
    /* Mientras el umbral no esté verificado contra la reglamentación, la
       pantalla NO puede decir «corriente» a secas. */
    fuente: u.fuente,
    /* Los asientos sin fecha no entran en la ventana: si hay, el conteo puede
       estar corto y el piloto tiene que saberlo. */
    sinFecha: t.sinFecha,
  };
}

/**
 * Agrupa el libro por mes, como las páginas del libro en papel.
 *
 * El libro en papel tiene una hoja por mes con su total al pie. Reproducirlo
 * hace que el piloto reconozca lo que ve y pueda cotejarlo contra el suyo.
 */
export function porMes(asientos = []) {
  const lista = Array.isArray(asientos) ? asientos : [];
  const meses = new Map();

  for (const a of lista) {
    if (!a?.fecha) continue;
    /* LOS ARRASTRES NO ARMAN MES.

       Un arrastre tiene fecha de CORTE, no de vuelo. Agrupado por mes aparecía
       como una hoja del libro con «0 vuelos» y dos mil horas — una hoja que el
       piloto no puede cotejar contra nada, porque ese mes no voló dos mil
       horas.

       Van aparte, con su propia lista. */
    if (!esVuelo(a)) continue;
    const mes = String(a.fecha).slice(0, 7);
    if (!meses.has(mes)) meses.set(mes, []);
    meses.get(mes).push(a);
  }

  return [...meses.entries()]
    .sort((x, y) => y[0].localeCompare(x[0]))
    .map(([mes, delMes]) => ({
      mes,
      asientos: [...delMes].sort((x, y) => String(y.fecha).localeCompare(String(x.fecha))),
      totales: totalesDeLibro(delMes, {}),
    }));
}

/**
 * Los arrastres, para mostrarlos aparte de los meses.
 *
 * El libro de papel los tiene al pie —«TOTAL LIBRO ECUADOR», «TOTAL LIBRO
 * BURKINA»— y no mezclados con las hojas mensuales. Acá igual: son saldos de
 * otros libros, no meses de este.
 */
export function arrastresDeLibro(asientos = []) {
  const lista = Array.isArray(asientos) ? asientos : [];
  return lista
    .filter((a) => a && !esVuelo(a))
    .sort((x, y) => String(x.fecha).localeCompare(String(y.fecha)));
}


/* ============================================================================
   ALTA MANUAL EN EL LIBRO

   El libro se llena solo al cerrar el cronómetro. Pero eso dejaba sin salida
   dos casos reales:

     · **El histórico.** Un piloto con años de vuelo no puede empezar su libro
       electrónico en cero: sus horas totales son su experiencia.
     · **El vuelo que no se cronometró.** Otra aeronave, otro operador, o
       simplemente el día que nadie apretó el botón.

   ══════════════════════════════════════════════════════════════════════════
   LO CARGADO A MANO SE MARCA COMO CARGADO A MANO

   No para desconfiar de él: para poder responder en una auditoría de dónde
   salió cada hora. Un vuelo cronometrado y uno transcrito del libro de papel
   valen lo mismo ante la autoridad, pero **no son la misma evidencia**.

   Y el ARRASTRE es distinto de un vuelo: un total anterior no es un vuelo con
   fecha y matrícula, es un saldo. Mezclarlos haría que el libro muestre un
   «vuelo» de 1200 horas.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/** Cómo entró cada asiento al libro. */
export const ENTRADA = {
  cronometro: "cronometro",
  manual: "manual",
  arrastre: "arrastre",
};

/**
 * Asiento cargado a mano.
 *
 * Mismas reglas que el automático —el FT es el número del piloto, los
 * aterrizajes van a día si no se declaran— más la marca de origen.
 */
export function asientoManual(datos = {}, { ahora = new Date() } = {}) {
  const base = asientoDePiloto(
    { ...datos, flightTime: num(datos?.flightTime) ?? deHhMm(datos?.flightTimeHhMm) },
    { ahora },
  );
  if (!base.asiento) return base;

  return {
    asiento: {
      ...base.asiento,
      entrada: ENTRADA.manual,
      origen: { ...(base.asiento.origen || {}), flightTime: "escrito" },
    },
    errores: [],
    avisos: base.avisos,
  };
}

/**
 * Saldo de horas anteriores al libro electrónico.
 *
 * NO es un vuelo: es un total. Se guarda con su propia marca para que el libro
 * pueda sumarlo sin mostrarlo como si alguien hubiera volado 1200 horas de una
 * sentada.
 */
export function asientoDeArrastre(datos = {}, { ahora = new Date() } = {}) {
  const errores = [];
  const avisosCoherencia = [];

  const horas = num(datos?.horas) ?? deHhMm(datos?.horasHhMm);
  if (horas === null || horas <= 0) {
    /* El mensaje tiene que decir qué se esperaba: «necesita las horas» con el
       campo lleno es lo que confundió al usuario. */
    errores.push(texto(datos?.horasHhMm) || num(datos?.horas) !== null
      ? `No se entiende «${texto(datos?.horasHhMm) || datos?.horas}» como horas. `
        + "Escribe el total en horas (11202) o en horas y minutos (11202:30)."
      : "El arrastre necesita las horas totales anteriores.");
  }

  const pilotId = texto(datos?.pilotId);
  if (!pilotId) errores.push("Falta el piloto.");

  /* La fecha de corte dice HASTA CUÁNDO cubre el arrastre. Sin ella no se
     puede saber si un vuelo cargado después ya estaba contado adentro, y se
     contaría dos veces. */
  const hasta = texto(datos?.hasta);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(hasta)) {
    errores.push("Falta la fecha hasta la que cubre el arrastre: sin ella no se "
      + "puede saber si un vuelo posterior ya estaba contado adentro.");
  }

  /* Ninguna columna de tipo de tiempo puede superar el total.

     Son de dos clases y las dos están acotadas por el total, aunque por
     motivos distintos:

       FUNCIONES (pic, sic, dual, instructor): son PARTES del total y además se
       excluyen entre sí.

       LO QUE SE HACE DURANTE EL VUELO (carga externa, cross country): no se
       excluyen con la función —un long line de 2:00 es 2:00 de PIC y 2:00 de
       carga externa— pero siguen siendo tiempo DENTRO del vuelo. Más carga
       externa que vuelo total es un error de transcripción. */
  if (horas !== null && horas > 0) {
    for (const c of ["pic", "sic", "dualRecibido", "solo", "instructor",
      "crossCountry", "cargaExterna"]) {
      const v = num(datos?.[c]) ?? deHhMm(datos?.[`${c}HhMm`]);
      if (v !== null && v > horas + 0.01) {
        const etiqueta = COLUMNAS_LIBRO.find((x) => x.clave === c)?.etiqueta || c;
        errores.push(`${etiqueta} (${aHhMm(v)}) supera el total (${aHhMm(horas)}). `
          + "Es una parte del total, no un tiempo aparte: revisa la transcripción.");
      }
    }
  }

  /* ¿El desglose por función cierra con el total?

     PIC, SIC, dual, solo e instructor son funciones EXCLUYENTES: en un vuelo
     se es una sola cosa. Su suma debería dar el total.

     Si sobra tiempo, puede ser correcto —vuelos sin función anotada en el libro
     viejo— pero también es lo que pasa cuando se olvidó una columna al
     transcribir. Se avisa con la diferencia: es un dato, no un veredicto. */
  /* Las funciones que se excluyen entre sí y suman al total.

     `cargaExterna` NO está: no es una función, es lo que se hace durante el
     vuelo. Sumarla acá haría que un vuelo de long line contara dos veces.

     `solo` se conserva por los asientos viejos que la tengan cargada. */
  const FUNCIONES_EXCLUYENTES = ["pic", "sic", "dualRecibido", "solo", "instructor"];
  const sumaFunciones = FUNCIONES_EXCLUYENTES
    .reduce((x, c) => x + (num(datos?.[c]) ?? deHhMm(datos?.[`${c}HhMm`]) ?? 0), 0);

  /* Tolerancia de dos minutos: las horas se guardan con dos decimales y cada
     columna redondea por su lado, así que sumar diez columnas puede correr el
     total un minuto sin que nadie se haya equivocado. Exigir igualdad exacta
     haría saltar el aviso por redondeo, y un aviso que salta cuando todo está
     bien se aprende a ignorar. */
  if (horas !== null && sumaFunciones > 0 && Math.abs(sumaFunciones - horas) > 2 / 60) {
    const dif = Math.abs(Math.round((horas - sumaFunciones) * 100) / 100);
    avisosCoherencia.push(
      sumaFunciones < horas
        ? `Las funciones suman ${aHhMm(sumaFunciones)} y el total es ${aHhMm(horas)}: `
          + `quedan ${aHhMm(dif)} sin asignar a ninguna función. Puede ser correcto, `
          + "pero revisa que no falte una columna del libro anterior."
        : `Las funciones suman ${aHhMm(sumaFunciones)}, más que el total de `
          + `${aHhMm(horas)}. En un vuelo se es una sola cosa: revisa el desglose.`,
    );
  }

  if (errores.length > 0) return { asiento: null, errores, avisos: avisosCoherencia };

  return {
    asiento: {
      fecha: hasta,
      pilotId,
      entrada: ENTRADA.arrastre,
      flightTime: horas,
      flightTimeHhMm: aHhMm(horas),
      /* Un arrastre no tiene aeronave ni función: es la suma de muchos vuelos
         distintos. Ponerle una sería inventar. */
      tipoAeronave: null,
      matricula: null,
      funcion: null,
      condicion: null,

      /* ═══ EL ARRASTRE TRAE TODAS LAS COLUMNAS ═══

         El libro del operador no arrastra un total: arrastra la fila completa
         —«TOTAL LIBRO ECUADOR», «TOTAL LIBRO BURKINA»— con sus horas de PIC,
         de instrumentos, de cross country y sus aterrizajes.

         Arrastrar solo el total dejaba el libro nuevo diciendo que el piloto
         tiene once mil horas y CERO como PIC. Para una licencia o una
         habilitación, ese desglose es justamente lo que se mira. */
      ...Object.fromEntries(COLUMNAS_HORAS
        .filter((c) => c !== "flightTime")
        .map((c) => [c, num(datos?.[c]) ?? deHhMm(datos?.[`${c}HhMm`]) ?? 0])),
      ...Object.fromEntries(COLUMNAS_ENTERAS.map((c) => [c, num(datos?.[c]) ?? 0])),
      appTipo: null,

      /* De qué libro viene. El piloto puede tener varios —uno por país o por
         operador— y sumarlos sin distinguir haría imposible cotejar contra el
         de papel. */
      libro: texto(datos?.libro) || null,

      observaciones: texto(datos?.observaciones)
        || `Total de ${texto(datos?.libro) || "libro anterior"}, hasta ${hasta}.`,
      origen: { flightTime: "escrito" },
      creado: (ahora instanceof Date ? ahora : new Date()).toISOString(),
    },
    errores: [],
    avisos: [
      ...avisosCoherencia,
      "El arrastre suma al total pero NO cuenta como vuelo: no aparece "
        + "en los períodos ni en la currency, porque no se sabe cuándo se voló cada hora.",
    ],
  };
}

/**
 * ¿Este asiento es un vuelo, o un saldo?
 *
 * Los períodos y la currency solo miran VUELOS: un arrastre no tiene fecha de
 * vuelo real, y contarlo en los últimos 90 días diría que el piloto voló mil
 * horas esta semana.
 */
export function esVuelo(asiento) {
  return Boolean(asiento) && asiento.entrada !== ENTRADA.arrastre;
}

/** Cómo entró un asiento, para mostrarlo. */
export function etiquetaDeEntrada(asiento) {
  if (!asiento) return null;
  if (asiento.entrada === ENTRADA.arrastre) return "Arrastre";
  if (asiento.entrada === ENTRADA.manual) return "Cargado a mano";
  return "Del cronómetro";
}


/* ============================================================================
   CORREGIR Y BORRAR ASIENTOS

   Faltaba, y el hueco se notó al primer uso real: el usuario cargó su arrastre
   con una versión que perdía las columnas, y después **no tenía forma de
   arreglarlo**. Solo podía volver a cargarlo, duplicando el total.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ EL LIBRO SÍ SE CORRIGE (Y EL SGS NO)

   El registro del SGS es inmutable: un reporte de seguridad no se edita porque
   la confianza del que reporta se basa en que lo escrito queda.

   El libro de vuelo es distinto. Es el registro del piloto sobre su propia
   actividad, y en papel se corrige tachando y anotando al margen. Lo que
   importa no es que sea inmutable, sino que **se sepa que se corrigió**: por
   eso cada corrección deja constancia de qué cambió y cuándo.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/** Identifica un asiento sin depender de un `id` que los viejos no tienen. */
export function claveDeAsiento(asiento) {
  if (!asiento) return null;
  if (asiento.id) return String(asiento.id);
  /* Los arrastres se identifican por libro + fecha de corte; los vuelos por
     fecha + matrícula + duración. No es infalible, pero es estable: sirve para
     encontrar el mismo asiento en la lista. */
  return !esVuelo(asiento)
    ? `arr|${asiento.libro || ""}|${asiento.fecha || ""}`
    : `vue|${asiento.fecha || ""}|${asiento.matricula || ""}|${asiento.flightTime ?? ""}`;
}

/**
 * Reemplaza un asiento por su versión corregida.
 *
 * Devuelve la lista completa: quien llama la guarda, igual que en el resto del
 * proyecto, para que el guardado se pueda confirmar antes de mostrar.
 */
export function corregirAsiento(lista, claveOriginal, datosNuevos, { ahora = new Date() } = {}) {
  const asientos = Array.isArray(lista) ? lista : [];
  const i = asientos.findIndex((a) => claveDeAsiento(a) === claveOriginal);

  if (i < 0) {
    return { lista: asientos, errores: ["No se encontró el asiento a corregir."], avisos: [] };
  }

  const original = asientos[i];
  const esArrastre = !esVuelo(original);

  const r = esArrastre
    ? asientoDeArrastre({ ...datosNuevos, pilotId: datosNuevos.pilotId ?? original.pilotId }, { ahora })
    : asientoManual({ ...datosNuevos, pilotId: datosNuevos.pilotId ?? original.pilotId }, { ahora });

  if (!r.asiento) return { lista: asientos, errores: r.errores, avisos: r.avisos || [] };

  /* La corrección DEJA CONSTANCIA. En el libro de papel se tacha y se anota al
     margen; acá queda qué cambió y cuándo, para que una auditoría pueda ver
     que el número se corrigió y no que siempre fue ese. */
  const cambios = [];
  for (const c of [...COLUMNAS_HORAS, ...COLUMNAS_ENTERAS]) {
    const antes = num(original[c]) ?? 0;
    const despues = num(r.asiento[c]) ?? 0;
    if (Math.abs(antes - despues) > 0.001) {
      cambios.push({ campo: c, antes, despues });
    }
  }

  const corregido = {
    ...r.asiento,
    id: original.id,
    correcciones: [
      ...(Array.isArray(original.correcciones) ? original.correcciones : []),
      { cuando: (ahora instanceof Date ? ahora : new Date()).toISOString(), cambios },
    ],
  };

  const nueva = [...asientos];
  nueva[i] = corregido;

  return {
    lista: nueva,
    errores: [],
    avisos: [
      ...(r.avisos || []),
      cambios.length > 0
        ? `Se corrigieron ${cambios.length} ${cambios.length === 1 ? "columna" : "columnas"}. `
          + "El cambio queda registrado en el asiento."
        : "No hubo cambios en las columnas.",
    ],
  };
}

/**
 * Quita un asiento de la lista.
 *
 * Un asiento cargado por error —duplicado, o del piloto equivocado— tiene que
 * poder salir: dejarlo obligaría a corregirlo a cero, y un arrastre en cero
 * sigue apareciendo en la tabla como si fuera un libro real.
 */
export function quitarAsiento(lista, clave) {
  const asientos = Array.isArray(lista) ? lista : [];
  const i = asientos.findIndex((a) => claveDeAsiento(a) === clave);

  if (i < 0) return { lista: asientos, errores: ["No se encontró el asiento."], quitado: null };

  return {
    lista: asientos.filter((_, j) => j !== i),
    errores: [],
    quitado: asientos[i],
  };
}

/** ¿Este asiento fue corregido alguna vez? */
export function fueCorregido(asiento) {
  return Array.isArray(asiento?.correcciones) && asiento.correcciones.length > 0;
}
